import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Download, Briefcase, IndianRupee, ArrowDownLeft, ArrowUpRight, TrendingUp, Calendar, Mail, FileText, DollarSign, Plus, CreditCard, Banknote, ArrowUpDown, ChevronUp, ChevronDown, UserPlus, RefreshCcw } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const API = '/api';

// ─── Left Panel: Project List ─────────────────────────────────────────
function ProjectListPanel({ projects, selectedId, onSelect, searchTerm, setSearchTerm }) {
  const filtered = useMemo(() => {
    if (!searchTerm) return projects;
    const q = searchTerm.toLowerCase();
    return projects.filter(p => p.name.toLowerCase().includes(q) || (p.client_name || '').toLowerCase().includes(q));
  }, [projects, searchTerm]);

  return (
    <div className="w-72 border-r border-stone-200 bg-white flex flex-col h-full" data-testid="project-accounts-list">
      <div className="p-3 border-b border-stone-200">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search projects..."
            className="pl-9 h-9 text-sm bg-stone-50"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="project-accounts-search"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map((p) => (
          <div
            key={p.id}
            onClick={() => onSelect(p.id)}
            className={`px-4 py-3 cursor-pointer border-b border-stone-100 transition-colors ${
              selectedId === p.id ? 'bg-stone-900 text-white' : 'hover:bg-stone-50'
            }`}
            data-testid={`project-item-${p.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className={`font-medium text-sm truncate ${selectedId === p.id ? 'text-white' : 'text-stone-900'}`}>
                  {p.name}
                </p>
                {p.client_name && (
                  <p className={`text-xs truncate ${selectedId === p.id ? 'text-stone-300' : 'text-stone-500'}`}>
                    {p.client_name}
                  </p>
                )}
              </div>
              <div className="text-right ml-2 flex-shrink-0">
                <p className={`text-xs font-semibold ${selectedId === p.id ? 'text-green-300' : 'text-stone-700'}`}>
                  {'\u20B9'}{p.net_balance.toLocaleString('en-IN')}
                </p>
                <p className={`text-[10px] ${selectedId === p.id ? 'text-stone-400' : 'text-stone-400'}`}>
                  net balance
                </p>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-stone-400">No projects found</div>
        )}
      </div>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────
function OverviewTab({ data, worksheetId }) {
  const navigate = useNavigate();
  // iter-310 reconciliation chip — sum of APPROVED Running Bills for this project,
  // compared against the BOQ Vendor Basic Total.
  const projectId = data?.project?.id;
  const [reconciliation, setReconciliation] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [billsR, boqR] = await Promise.all([
          axios.get(`${API}/vendor-submissions`, { params: { project_id: projectId, submission_type: 'bill', status: 'approved' }, headers }),
          axios.get(`${API}/boq-worksheets`, { params: { project_id: projectId }, headers }),
        ]);
        const billsTotal = (billsR.data || []).reduce((a, b) => a + (b.grand_total || 0), 0);
        const ws = (boqR.data || []).filter(w => w.project_id === projectId);
        // Vendor basic total = sum of (qty * vendor_rate) across all worksheets/items.
        // iter-338c (Msg-680 Bug 6): for ad-hoc imports the Est columns are
        // intentionally zero — use the Actual side (actual_quantity *
        // actual_vendor_rate) when Est is zero so the Vendor total no
        // longer shows ₹0 for project that have ad-hoc vendor BOQs.
        let vendorTotal = 0;
        for (const w of ws) {
          for (const it of (w.items || [])) {
            if (it.item_type === 'header' || it.is_header || it.is_discount) continue;
            const estQ = +it.quantity || 0;
            const estVR = +(it.vendor_rate ?? it.rate ?? 0);
            const estLine = estQ * estVR;
            if (estLine > 0) {
              vendorTotal += estLine;
              continue;
            }
            // ad-hoc fallback
            const aQ = +(it.actual_quantity ?? 0);
            const aVR = +(it.actual_vendor_rate ?? it.actual_rate ?? 0);
            vendorTotal += aQ * aVR;
          }
        }
        setReconciliation({ billsTotal, vendorTotal, billsCount: (billsR.data || []).length });
      } catch {/* silent */}
    })();
  }, [projectId]);

  if (!data) return <div className="p-8 text-center text-stone-400">Loading...</div>;
  const { project, financial_summary: fs, progress } = data;
  const pct = reconciliation && reconciliation.vendorTotal > 0
    ? Math.min(100, Math.round((reconciliation.billsTotal / reconciliation.vendorTotal) * 100))
    : 0;
  const overrun = reconciliation && reconciliation.billsTotal > reconciliation.vendorTotal;

  return (
    <div className="p-6 space-y-6" data-testid="project-overview-tab">
      {/* iter-386u-c — Project Overview section relocated from ProjectDetail.js
          so admins see project header info directly on the accounts screen. */}
      <div className="bg-white border border-stone-200 rounded-xl p-6" data-testid="project-overview-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-playfair text-2xl font-bold text-stone-900" data-testid="project-title">
              {project.name || '—'}
            </h2>
            {project.client_name && (
              <p className="text-sm text-stone-500 mt-1">Client: <span className="font-medium text-stone-700">{project.client_name}</span></p>
            )}
          </div>
          {project.status && (
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200"
              data-testid="project-status"
            >
              {project.status.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        {project.description && (
          <p className="text-stone-600 text-sm mt-3" data-testid="project-description">{project.description}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {project.budget !== undefined && project.budget !== null && (
            <div className="flex items-center gap-3">
              <div className="p-2 bg-stone-100 rounded"><IndianRupee size={20} className="text-stone-800" /></div>
              <div>
                <p className="text-xs text-stone-500">Budget</p>
                <p className="text-base font-bold text-stone-900" data-testid="project-budget">
                  {'\u20B9'}{Number(project.budget || 0).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded"><Calendar size={20} className="text-blue-600" /></div>
            <div>
              <p className="text-xs text-stone-500">Start Date</p>
              <p className="text-base font-bold text-stone-900" data-testid="project-start-date">
                {project.start_date || '—'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded"><Calendar size={20} className="text-red-600" /></div>
            <div>
              <p className="text-xs text-stone-500">End Date</p>
              <p className="text-base font-bold text-stone-900" data-testid="project-end-date">
                {project.end_date || '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top cards */}
      <div className="grid grid-cols-4 gap-4">
        <button
          type="button"
          onClick={() => worksheetId && navigate(`/boq-worksheets/${worksheetId}/edit`)}
          disabled={!worksheetId}
          className="bg-blue-50 rounded-lg p-4 border border-blue-100 text-left transition-all hover:border-blue-400 hover:shadow-md disabled:cursor-default disabled:opacity-90 group"
          data-testid="overview-contract-value"
          title={worksheetId ? 'Open the Edit BOQ page for this project' : 'No BOQ found — create one first'}
        >
          <p className="text-xs text-blue-600 mb-1 flex items-center gap-1">
            Contract Value
            {worksheetId && <span className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500">↗</span>}
          </p>
          <p className="text-xl font-bold text-blue-900">{'\u20B9'}{fs.contract_value.toLocaleString('en-IN')}</p>
          <p className="text-xs text-blue-500 mt-1">{progress.percentage}% complete{worksheetId ? ' · Open BOQ →' : ''}</p>
        </button>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <p className="text-xs text-green-600 mb-1">Client Received</p>
          <p className="text-xl font-bold text-green-900">{'\u20B9'}{fs.total_received.toLocaleString('en-IN')}</p>
          <div className="flex gap-1.5 mt-1">
            <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Online: {'\u20B9'}{(fs.client_online || 0).toLocaleString('en-IN')}</span>
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Cash: {'\u20B9'}{(fs.client_cash || 0).toLocaleString('en-IN')}</span>
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-100">
          <p className="text-xs text-red-600 mb-1">Vendor Paid</p>
          <p className="text-xl font-bold text-red-900">{'\u20B9'}{fs.total_paid.toLocaleString('en-IN')}</p>
          <div className="flex gap-1.5 mt-1">
            <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Online: {'\u20B9'}{(fs.vendor_online || 0).toLocaleString('en-IN')}</span>
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Cash: {'\u20B9'}{(fs.vendor_cash || 0).toLocaleString('en-IN')}</span>
          </div>
        </div>
        <div className={`rounded-lg p-4 border ${fs.available_fund >= 0 ? 'bg-stone-900' : 'bg-red-100 border-red-200'}`}>
          <p className={`text-xs mb-1 ${fs.available_fund >= 0 ? 'text-stone-400' : 'text-red-600'}`}>Available Fund</p>
          <p className={`text-xl font-bold ${fs.available_fund >= 0 ? 'text-white' : 'text-red-900'}`}>{'\u20B9'}{fs.available_fund.toLocaleString('en-IN')}</p>
          <div className="flex gap-1.5 mt-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${fs.available_fund >= 0 ? 'bg-stone-800 text-stone-300' : 'bg-red-50 text-red-700'}`}>Online: {'\u20B9'}{(fs.available_online || 0).toLocaleString('en-IN')}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${fs.available_fund >= 0 ? 'bg-stone-800 text-stone-300' : 'bg-amber-50 text-amber-700'}`}>Cash: {'\u20B9'}{(fs.available_cash || 0).toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>

      {/* iter-310: Running-Bill Reconciliation chip */}
      {reconciliation && (reconciliation.billsCount > 0 || reconciliation.vendorTotal > 0) && (
        <div
          className={`rounded-lg p-4 border ${overrun ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
          data-testid="running-bill-reconciliation"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide ${overrun ? 'text-red-700' : 'text-emerald-700'}`}>
                Running-Bill Reconciliation
              </p>
              <p className={`text-sm font-medium mt-0.5 ${overrun ? 'text-red-900' : 'text-emerald-900'}`}>
                Approved bills <strong>Rs {Math.round(reconciliation.billsTotal).toLocaleString('en-IN')}</strong>
                {reconciliation.vendorTotal > 0 ? <> of Rs {Math.round(reconciliation.vendorTotal).toLocaleString('en-IN')} vendor budget · <strong>{pct}%</strong></> : <> (no BOQ vendor budget set yet)</>}
                {' · '}{reconciliation.billsCount} bill{reconciliation.billsCount === 1 ? '' : 's'} approved
              </p>
            </div>
            {reconciliation.vendorTotal > 0 && (
              <div className="flex-1 max-w-md">
                <div className="h-2 bg-white border border-stone-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${overrun ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          {overrun && (
            <p className="text-[11px] text-red-700 mt-2">
              Approved bills exceed BOQ vendor budget by Rs {Math.round(reconciliation.billsTotal - reconciliation.vendorTotal).toLocaleString('en-IN')}. Review with vendor.
            </p>
          )}
        </div>
      )}

      {/* Additional Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${fs.balance_due > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-100'}`}>
          <p className={`text-xs mb-1 ${fs.balance_due > 0 ? 'text-amber-600' : 'text-green-600'}`}>Balance Due from Client</p>
          <p className={`text-2xl font-bold ${fs.balance_due > 0 ? 'text-amber-900' : 'text-green-900'}`}>{'\u20B9'}{fs.balance_due.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-stone-200">
          <p className="text-xs text-stone-500 mb-1">Profit Margin</p>
          <p className={`text-2xl font-bold ${fs.profit_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>{'\u20B9'}{fs.profit_margin.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-stone-200">
          <p className="text-xs text-stone-500 mb-1">Project Progress</p>
          <p className="text-2xl font-bold text-stone-900">{progress.percentage}%</p>
          <p className="text-xs text-stone-500 mt-1">{progress.completed} of {progress.total_tasks} tasks</p>
        </div>
      </div>

      {/* Project Info */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
          <Briefcase size={14} /> Project Details
        </h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
          <div><span className="text-stone-500">Client:</span> <span className="text-stone-800 font-medium ml-2">{project.client_name || '-'}</span></div>
          <div><span className="text-stone-500">Status:</span> <Badge variant="outline" className="ml-2 text-xs">{project.status?.replace(/_/g, ' ')}</Badge></div>
          {project.start_date && <div><span className="text-stone-500">Start:</span> <span className="text-stone-800 ml-2">{project.start_date}</span></div>}
          {project.end_date && <div><span className="text-stone-500">End:</span> <span className="text-stone-800 ml-2">{project.end_date}</span></div>}
        </div>
      </div>
    </div>
  );
}

// Helper to format date as DD-MM-YYYY
const fmtDate = (d) => {
  if (!d || d.length < 10) return d || '';
  const parts = d.substring(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : d;
};

// ─── Transactions Tab ─────────────────────────────────────────────────
function TransactionsTab({ projectId }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [txData, setTxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  // Sorting state
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // New Transaction dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [txType, setTxType] = useState('client'); // 'client' or 'vendor'
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentMode, setPaymentMode] = useState('online');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [processing, setProcessing] = useState(false);
  
  // Vendor fields
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [showCustomVendor, setShowCustomVendor] = useState(false);
  const [showCreateVendorForm, setShowCreateVendorForm] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', email: '', phone: '', company_name: '' });
  const [creatingVendor, setCreatingVendor] = useState(false);

  // Fund ledger for payment stages
  const [fundLedger, setFundLedger] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);

  // iter-304: Inline edit modal state (replaces black-screen navigation
  // to /payments when a transaction row was clicked).
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editMode, setEditMode] = useState('online');
  const [editDate, setEditDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editVendorId, setEditVendorId] = useState('');
  const [editVendorName, setEditVendorName] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editProcessing, setEditProcessing] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);

  const openEditDialog = useCallback(async (tx) => {
    if (!tx || !tx.id) {
      toast.error('This transaction cannot be edited');
      return;
    }
    setEditingTx(tx);
    setEditAmount(String(tx.amount || ''));
    setEditMode(tx.mode || 'online');
    setEditDate(tx.date ? tx.date.substring(0, 10) : new Date().toISOString().split('T')[0]);
    setEditNotes(tx.notes || '');
    setEditVendorId(tx.vendor_id || '');
    setEditVendorName(tx.party_name || '');
    setEditReason('');
    setEditDialogOpen(true);
    if (tx.type === 'paid' && vendors.length === 0) {
      try {
        const vResp = await axios.get(`${API}/vendors/directory`, { headers: { Authorization: `Bearer ${token}` } });
        setVendors(vResp.data || []);
      } catch {}
    }
  }, [token, vendors.length]);

  const handleSaveEdit = async () => {
    if (!editReason || editReason.trim().length < 3) {
      toast.error('Please provide a reason for this edit (min 3 characters)');
      return;
    }
    if (!editAmount || parseFloat(editAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setEditProcessing(true);
    try {
      await axios.put(`${API}/payments/${editingTx.id}/amend`, {
        amount: parseFloat(editAmount),
        notes: editNotes,
        payment_mode: editMode,
        payment_date: editDate ? new Date(editDate).toISOString() : null,
        vendor_id: editingTx.type === 'paid' ? editVendorId : null,
        vendor_name: editingTx.type === 'paid' ? editVendorName : null,
        amend_reason: editReason.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction updated');
      setEditDialogOpen(false);
      setEditingTx(null);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setEditProcessing(false);
    }
  };

  const handleDeleteTx = async () => {
    if (!editingTx?.id) return;
    if (!window.confirm('Are you sure you want to delete this transaction? This cannot be undone.')) return;
    setEditDeleting(true);
    try {
      await axios.delete(`${API}/payments/${editingTx.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction deleted');
      setEditDialogOpen(false);
      setEditingTx(null);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    } finally {
      setEditDeleting(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, [projectId]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/project-accounts/${projectId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTxData(resp.data);
    } catch { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  };

  // Fetch vendors and fund ledger when dialog opens
  const openDialog = useCallback(async () => {
    setDialogOpen(true);
    setTxType('client');
    setPaymentAmount('');
    setPaymentNotes('');
    setPaymentMode('online');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setVendorId('');
    setVendorName('');
    setShowCustomVendor(false);
    setShowCreateVendorForm(false);
    setNewVendor({ name: '', email: '', phone: '', company_name: '' });
    setSelectedStage(null);
    try {
      const [vendorsResp, ledgerResp] = await Promise.all([
        axios.get(`${API}/vendors/directory`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/${projectId}/fund-ledger`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setVendors(vendorsResp.data || []);
      setFundLedger(ledgerResp.data || null);
    } catch (e) { /* silent - dialog still works without stages */ }
  }, [projectId, token]);

  // Create new vendor inline (iter-304b: adds confirmation dialog + toast + list refresh)
  const handleCreateVendorInline = async () => {
    if (!newVendor.name?.trim()) { toast.error('Vendor name is required'); return; }
    // iter-304b: Confirmation dialog before creating (user-visible summary)
    const summary = `Create new vendor?\n\nName: ${newVendor.name.trim()}` +
      (newVendor.email ? `\nEmail: ${newVendor.email.trim()}` : '\nEmail: (auto-generated)') +
      (newVendor.phone ? `\nPhone: ${newVendor.phone}` : '') +
      (newVendor.company_name ? `\nCompany: ${newVendor.company_name}` : '') +
      `\n\nThis vendor will be added to the directory and auto-selected in the payment form.`;
    if (!window.confirm(summary)) return;
    setCreatingVendor(true);
    try {
      const vendorEmail = newVendor.email?.trim() || `vendor_${Date.now()}@placeholder.bellacasa.in`;
      const resp = await axios.post(`${API}/auth/register`, {
        name: newVendor.name.trim(),
        email: vendorEmail,
        password: `Vendor${Date.now()}!`,
        role: 'vendor',
        phone: newVendor.phone || '',
        organization: newVendor.company_name || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      const createdUser = resp.data.user;
      if (newVendor.company_name) {
        try {
          await axios.patch(`${API}/users/${createdUser.id}`, {
            company_name: newVendor.company_name,
            master_data: { company_name: newVendor.company_name }
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch {}
      }
      // iter-304b: richer success toast mentioning auto-select + directory add
      toast.success(`Vendor "${createdUser.name}" added to directory & selected for this payment`, { duration: 5000 });
      setVendorId(createdUser.id);
      setVendorName(createdUser.name);
      setShowCreateVendorForm(false);
      setShowCustomVendor(false);
      setNewVendor({ name: '', email: '', phone: '', company_name: '' });
      // Refresh directory so the new vendor appears in subsequent selections
      try {
        const vResp = await axios.get(`${API}/vendors/directory`, { headers: { Authorization: `Bearer ${token}` } });
        setVendors(vResp.data || []);
      } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create vendor');
    } finally { setCreatingVendor(false); }
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (txType === 'vendor' && !vendorId && !vendorName) {
      toast.error('Please select or enter a vendor name');
      return;
    }
    setProcessing(true);
    try {
      if (txType === 'client') {
        await axios.post(`${API}/projects/${projectId}/record-client-payment`, {
          amount: parseFloat(paymentAmount),
          notes: paymentNotes || 'Payment recorded via Project Accounts',
          payment_type: 'client_received',
          payment_mode: paymentMode,
          payment_date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString()
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Client payment of ₹${parseFloat(paymentAmount).toLocaleString('en-IN')} recorded`);
      } else {
        await axios.post(`${API}/projects/${projectId}/record-vendor-payment`, {
          amount: parseFloat(paymentAmount),
          vendor_name: vendorName || '',
          vendor_id: vendorId || '',
          notes: paymentNotes || '',
          payment_type: 'vendor_paid',
          payment_mode: paymentMode,
          payment_date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString()
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Vendor payment of ₹${parseFloat(paymentAmount).toLocaleString('en-IN')} recorded`);
      }
      setDialogOpen(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessing(false);
    }
  };

  // Sorting helper
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'date' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="ml-1 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />;
  };

  const filtered = useMemo(() => {
    if (!txData) return [];
    let list = filterType === 'all' ? [...txData.transactions] : txData.transactions.filter(t => t.type === filterType);
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'date': cmp = new Date(a.date || 0) - new Date(b.date || 0); break;
        case 'party': cmp = (a.party_name || '').localeCompare(b.party_name || ''); break;
        case 'type': cmp = (a.type || '').localeCompare(b.type || ''); break;
        case 'mode': cmp = (a.mode || '').localeCompare(b.mode || ''); break;
        case 'amount': cmp = (a.amount || 0) - (b.amount || 0); break;
        default: cmp = 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [txData, filterType, sortField, sortDir]);

  if (loading) return <div className="p-8 text-center text-stone-400">Loading transactions...</div>;

  return (
    <div className="p-6" data-testid="project-transactions-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-stone-800">All Transactions</h3>
          <p className="text-xs text-stone-500">
            {filtered.length} transactions | 
            Received: {'\u20B9'}{(txData?.total_received || 0).toLocaleString('en-IN')} | 
            Paid: {'\u20B9'}{(txData?.total_paid || 0).toLocaleString('en-IN')} | 
            Net: {'\u20B9'}{(txData?.net || 0).toLocaleString('en-IN')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openDialog} className="bg-stone-800 hover:bg-stone-900 text-white gap-1.5" data-testid="new-transaction-btn">
            <Plus size={14} />
            New Transaction
          </Button>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="project-txn-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="received">Client Received</SelectItem>
              <SelectItem value="paid">Vendor Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length > 0 ? (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-stone-600">
              <tr>
                {[
                  { key: 'date', label: 'Date', align: 'text-left' },
                  { key: 'party', label: 'Party', align: 'text-left' },
                  { key: 'type', label: 'Type', align: 'text-left' },
                  { key: 'mode', label: 'Mode', align: 'text-left' },
                  { key: 'notes', label: 'Notes', align: 'text-left', sortable: false },
                  { key: 'amount', label: `Amount (\u20B9)`, align: 'text-right' },
                ].map(col => (
                  <th
                    key={col.key}
                    className={`p-3 ${col.align} ${col.sortable !== false ? 'cursor-pointer hover:bg-stone-200 select-none transition-colors' : ''}`}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                    data-testid={`txn-sort-${col.key}`}
                  >
                    <span className="inline-flex items-center">
                      {col.label}
                      {col.sortable !== false && <SortIcon field={col.key} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr
                  key={i}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                  data-testid={`project-txn-row-${i}`}
                  onClick={() => openEditDialog(t)}
                >
                  <td className="p-3 text-stone-700 whitespace-nowrap">{fmtDate(t.date)}</td>
                  <td className="p-3 text-stone-800 font-medium">
                    {t.vendor_id ? (
                      <button
                        type="button"
                        className="text-blue-700 hover:underline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/vendors/${t.vendor_id}`); }}
                        data-testid={`project-txn-party-link-${i}`}
                      >
                        {t.party_name || '-'}
                      </button>
                    ) : (t.party_name || '-')}
                  </td>
                  <td className="p-3">
                    {(() => {
                      // iter-386u-x — dedicated badges for running_bill and credit_note.
                      const badge = t.type === 'received'
                        ? { cls: 'bg-green-100 text-green-700', icon: <ArrowDownLeft size={12} />, label: 'Received' }
                        : t.type === 'running_bill'
                          ? { cls: 'bg-purple-100 text-purple-700', icon: <FileText size={12} />, label: 'Billed' }
                          : t.type === 'credit_note'
                            ? { cls: 'bg-amber-100 text-amber-700', icon: <RefreshCcw size={12} />, label: 'Credit Note' }
                            : { cls: 'bg-red-100 text-red-700', icon: <ArrowUpRight size={12} />, label: 'Paid' };
                      return (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
                          {badge.icon}
                          {badge.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="p-3 text-stone-600">{t.mode}</td>
                  <td className="p-3 text-stone-500 truncate max-w-[200px]">{t.notes || '-'}</td>
                  <td className={`p-3 text-right font-semibold ${
                    t.type === 'received' ? 'text-green-700'
                    : t.type === 'running_bill' ? 'text-purple-700'
                    : t.type === 'credit_note' ? 'text-amber-700'
                    : 'text-red-700'
                  }`}>
                    {t.type === 'paid' ? '-' : t.type === 'received' ? '+' : ''}{'\u20B9'}{t.amount.toLocaleString('en-IN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-lg p-10 text-center text-stone-400">
          <DollarSign size={32} className="mx-auto mb-2 text-stone-300" />
          No transactions found for this project.
        </div>
      )}

      {/* ── Edit Transaction Dialog (iter-304 inline edit) ── */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-txn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingTx?.type === 'received' ? <ArrowDownLeft size={18} className="text-green-600" /> : <ArrowUpRight size={18} className="text-red-600" />}
              Edit {editingTx?.type === 'received' ? 'Client Payment' : 'Vendor Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-medium mb-1 block">Amount ({'\u20B9'})</Label>
              <Input
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                data-testid="edit-txn-amount"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium mb-1 block">Mode</Label>
                <Select value={editMode} onValueChange={setEditMode}>
                  <SelectTrigger data-testid="edit-txn-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">Date</Label>
                <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} data-testid="edit-txn-date" />
              </div>
            </div>
            {editingTx?.type === 'paid' && (
              <div>
                <Label className="text-xs font-medium mb-1 block">Vendor</Label>
                <Select value={editVendorId || 'custom'} onValueChange={(v) => {
                  if (v === 'custom') { setEditVendorId(''); return; }
                  const sel = vendors.find(x => x.id === v);
                  setEditVendorId(v);
                  if (sel) setEditVendorName(sel.name);
                }}>
                  <SelectTrigger data-testid="edit-txn-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom / Other</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="mt-2"
                  value={editVendorName}
                  onChange={(e) => setEditVendorName(e.target.value)}
                  placeholder="Vendor name"
                  data-testid="edit-txn-vendor-name"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium mb-1 block">Notes</Label>
              <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} data-testid="edit-txn-notes" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block text-amber-700">Reason for edit <span className="text-red-500">*</span></Label>
              <Input
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="e.g. Corrected amount, wrong date, typo in notes"
                data-testid="edit-txn-reason"
              />
              <p className="text-[11px] text-stone-500 mt-1">Audit-logged. Minimum 3 characters.</p>
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleDeleteTx}
                disabled={editDeleting || editProcessing}
                data-testid="edit-txn-delete"
              >
                {editDeleting ? 'Deleting...' : 'Delete'}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editProcessing || editDeleting} data-testid="edit-txn-cancel">Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={editProcessing || editDeleting} className="bg-stone-800 hover:bg-stone-900 text-white" data-testid="edit-txn-save">
                  {editProcessing ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── New Transaction Dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={18} />
              Record New Transaction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Type toggle */}
            <div className="flex rounded-lg border border-stone-200 overflow-hidden" data-testid="txn-type-toggle">
              <button
                type="button"
                onClick={() => { setTxType('client'); setSelectedStage(null); setPaymentAmount(''); setPaymentNotes(''); setVendorId(''); setVendorName(''); setShowCreateVendorForm(false); setShowCustomVendor(false); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-all ${
                  txType === 'client' ? 'bg-green-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'
                }`}
                data-testid="txn-type-client"
              >
                <ArrowDownLeft size={14} className="inline mr-1.5" />
                Client Received
              </button>
              <button
                type="button"
                onClick={() => { setTxType('vendor'); setSelectedStage(null); setPaymentAmount(''); setPaymentNotes(''); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-all ${
                  txType === 'vendor' ? 'bg-red-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'
                }`}
                data-testid="txn-type-vendor"
              >
                <ArrowUpRight size={14} className="inline mr-1.5" />
                Vendor Paid
              </button>
            </div>

            {/* Info banner */}
            <div className={`p-3 rounded-lg border text-sm ${
              txType === 'client' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              {txType === 'client'
                ? 'Record payment received from client. Select a payment stage or enter a custom amount.'
                : 'Record payment made to vendor/contractor. Select a vendor and enter the amount.'}
            </div>

            {/* Client: Payment Stages */}
            {txType === 'client' && fundLedger?.payment_stages && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Select Payment Stage</Label>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto border rounded-lg p-2 bg-stone-50">
                  {fundLedger.payment_stages.map((stage) => {
                    const isPaid = stage.paid === true || stage.status === 'paid';
                    const isDisabled = isPaid;
                    return (
                      <div
                        key={stage.name}
                        onClick={() => {
                          if (!isDisabled) {
                            setSelectedStage(stage);
                            setPaymentAmount(stage.amount?.toString() || '');
                            setPaymentNotes(`${stage.label} - ${stage.description || ''}`);
                          }
                        }}
                        className={`p-2.5 rounded-lg border transition-all ${
                          isDisabled ? 'border-stone-200 bg-stone-100 cursor-not-allowed opacity-60'
                            : selectedStage?.name === stage.name ? 'border-green-500 bg-green-50 ring-2 ring-green-200 cursor-pointer'
                            : 'border-stone-200 bg-white hover:border-green-300 cursor-pointer'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className={`font-medium text-sm ${isDisabled ? 'text-stone-400' : 'text-stone-800'}`}>
                              {stage.label}
                              {isPaid && <span className="ml-2 text-xs text-green-600">✓ Paid</span>}
                            </p>
                            {stage.description && <p className="text-xs text-stone-500">{stage.description}</p>}
                          </div>
                          <div className="text-right">
                            <p className={`font-bold text-sm ${isDisabled ? 'text-stone-400' : 'text-green-700'}`}>
                              ₹{stage.amount?.toLocaleString('en-IN')}
                            </p>
                            <p className="text-xs text-stone-400">{stage.percentage}%</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Vendor: Vendor selector */}
            {txType === 'vendor' && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Vendor / Contractor Name *</Label>
                {showCreateVendorForm ? (
                  <div className="border border-stone-200 rounded-lg p-3 space-y-3 bg-stone-50">
                    <p className="text-xs font-semibold text-stone-700 flex items-center gap-1"><UserPlus size={12} /> Create New Vendor</p>
                    <Input
                      value={newVendor.name}
                      onChange={(e) => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Vendor Name *"
                      autoFocus
                      data-testid="new-vendor-name"
                    />
                    <Input
                      value={newVendor.email}
                      onChange={(e) => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Email (optional)"
                      type="email"
                      data-testid="new-vendor-email"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={newVendor.phone}
                        onChange={(e) => setNewVendor(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="Phone"
                        data-testid="new-vendor-phone"
                      />
                      <Input
                        value={newVendor.company_name}
                        onChange={(e) => setNewVendor(prev => ({ ...prev, company_name: e.target.value }))}
                        placeholder="Company Name"
                        data-testid="new-vendor-company"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleCreateVendorInline} disabled={creatingVendor || !newVendor.name?.trim()} className="bg-green-600 hover:bg-green-700" data-testid="confirm-create-vendor">
                        {creatingVendor ? 'Creating...' : 'Create & Select'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setShowCreateVendorForm(false); setNewVendor({ name: '', email: '', phone: '', company_name: '' }); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : !showCustomVendor ? (
                  <select
                    value={vendorId}
                    onChange={(e) => {
                      if (e.target.value === '__create__') {
                        setShowCreateVendorForm(true);
                      } else if (e.target.value === '__custom__') {
                        setVendorName('');
                        setVendorId('');
                        setShowCustomVendor(true);
                      } else {
                        const v = vendors.find(v => v.id === e.target.value);
                        setVendorId(e.target.value);
                        setVendorName(v?.name || v?.company_name || '');
                      }
                    }}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-500"
                    data-testid="txn-vendor-select"
                  >
                    <option value="">Select vendor...</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}{v.company_name ? ` (${v.company_name})` : ''}</option>
                    ))}
                    <option value="__create__" style={{ fontWeight: 600, color: '#16a34a' }}>+ Create New Vendor</option>
                    <option value="__custom__">Other (type manually)</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={vendorName}
                      onChange={(e) => setVendorName(e.target.value)}
                      placeholder="Enter vendor/contractor name"
                      data-testid="txn-vendor-name-input"
                      autoFocus
                    />
                    <Button variant="outline" size="sm" onClick={() => { setShowCustomVendor(false); setVendorName(''); setVendorId(''); }}>
                      Back
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Vendor: Quick Amount Selection (payment slabs) */}
            {txType === 'vendor' && fundLedger?.payment_stages && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Quick Amount (based on contract stages)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {fundLedger.payment_stages.filter(s => s.name !== 'retention').slice(0, 4).map((stage) => {
                    const vendorPaid = txData?.total_paid || 0;
                    const stageIdx = fundLedger.payment_stages.findIndex(s => s.name === stage.name);
                    let cumulative = 0;
                    for (let i = 0; i <= stageIdx; i++) {
                      if (fundLedger.payment_stages[i].name !== 'retention') cumulative += fundLedger.payment_stages[i].amount;
                    }
                    const isPaidForVendor = vendorPaid >= cumulative;
                    return (
                      <button
                        key={stage.name}
                        type="button"
                        disabled={isPaidForVendor}
                        onClick={() => {
                          if (!isPaidForVendor) {
                            setPaymentAmount(stage.amount.toString());
                            setPaymentNotes(`Vendor payment - ${stage.label}`);
                          }
                        }}
                        className={`p-2 text-left rounded-lg border transition-all ${
                          isPaidForVendor ? 'border-stone-200 bg-stone-100 cursor-not-allowed opacity-60'
                            : paymentAmount === stage.amount.toString() ? 'border-red-500 bg-red-50'
                            : 'border-stone-200 bg-white hover:border-red-300 hover:bg-red-50/50'
                        }`}
                        data-testid={`vendor-quick-amount-${stage.name}`}
                      >
                        <p className={`font-medium text-sm ${isPaidForVendor ? 'text-stone-400' : 'text-stone-700'}`}>
                          ₹{stage.amount?.toLocaleString('en-IN')}
                          {isPaidForVendor && <span className="ml-1 text-green-600 text-xs">✓ Paid</span>}
                        </p>
                        <p className="text-xs text-stone-500">{stage.percentage}% - {stage.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Available Fund indicator */}
            {txType === 'vendor' && fundLedger && (
              <p className="text-xs text-stone-500">
                Available Fund: ₹{(fundLedger.available_fund || fundLedger.total_received || 0).toLocaleString('en-IN')}
              </p>
            )}

            {/* Amount */}
            <div>
              <Label className="text-sm font-medium">Amount ({'\u20B9'}) *</Label>
              <Input
                type="number"
                min="0"
                step="100"
                value={paymentAmount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={txType === 'client' ? 'Enter amount received' : 'Enter amount paid'}
                className="mt-1"
                data-testid="txn-amount"
              />
            </div>

            {/* Payment Mode */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Payment Mode *</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMode('online')}
                  className={`flex-1 p-2.5 rounded-lg border-2 transition-all ${
                    paymentMode === 'online'
                      ? (txType === 'client' ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700')
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                  }`}
                  data-testid="txn-mode-online"
                >
                  <CreditCard size={16} className="mx-auto" />
                  <span className="block text-xs font-medium mt-1">Online</span>
                  <span className="block text-[10px] text-stone-400">Bank Transfer / UPI / Card</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMode('cash')}
                  className={`flex-1 p-2.5 rounded-lg border-2 transition-all ${
                    paymentMode === 'cash'
                      ? (txType === 'client' ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700')
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                  }`}
                  data-testid="txn-mode-cash"
                >
                  <Banknote size={16} className="mx-auto" />
                  <span className="block text-xs font-medium mt-1">Cash</span>
                  <span className="block text-[10px] text-stone-400">Physical Cash Payment</span>
                </button>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium">Notes (optional)</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder={txType === 'client' ? 'e.g., Invoice #123, Stage 2' : 'e.g., Material purchase, PO #456'}
                className="mt-1"
                data-testid="txn-notes"
              />
            </div>

            {/* Date */}
            <div>
              <Label className="text-sm font-medium">Payment Date *</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
                data-testid="txn-date"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="txn-cancel-btn">Cancel</Button>
            <Button
              onClick={handleRecordPayment}
              disabled={processing || !paymentAmount || parseFloat(paymentAmount) <= 0 || (txType === 'vendor' && !vendorId && !vendorName)}
              className={txType === 'client' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="txn-record-btn"
            >
              {processing ? 'Recording...' : 'Record Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Statement Tab ────────────────────────────────────────────────────
function StatementTab({ projectId }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dateRange, setDateRange] = useState('all_time');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stmtFilter, setStmtFilter] = useState('all'); // 'all', 'received', 'paid'
  const [stmtSortOrder, setStmtSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [savedRanges, setSavedRanges] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`saved_stmt_ranges_${projectId}`) || '[]'); } catch { return []; }
  });
  const [savingRange, setSavingRange] = useState(false);
  const [rangeName, setRangeName] = useState('');

  const saveCustomRange = () => {
    if (!rangeName.trim() || !startDate || !endDate) return;
    const newRange = { name: rangeName.trim(), start: startDate, end: endDate, id: Date.now().toString() };
    const updated = [...savedRanges, newRange];
    setSavedRanges(updated);
    localStorage.setItem(`saved_stmt_ranges_${projectId}`, JSON.stringify(updated));
    setRangeName('');
    setSavingRange(false);
    toast.success(`Range "${newRange.name}" saved`);
  };

  const deleteSavedRange = (id) => {
    const updated = savedRanges.filter(r => r.id !== id);
    setSavedRanges(updated);
    localStorage.setItem(`saved_stmt_ranges_${projectId}`, JSON.stringify(updated));
  };

  const getDateRange = (preset) => {
    const now = new Date();
    let s, e;
    // Check saved ranges
    const savedMatch = savedRanges.find(r => r.id === preset);
    if (savedMatch) return { start: savedMatch.start, end: savedMatch.end };
    switch (preset) {
      case 'this_month':
        s = new Date(now.getFullYear(), now.getMonth(), 1); e = now; break;
      case 'last_month':
        s = new Date(now.getFullYear(), now.getMonth() - 1, 1); e = new Date(now.getFullYear(), now.getMonth(), 0); break;
      case 'this_quarter':
        const qm = Math.floor(now.getMonth() / 3) * 3;
        s = new Date(now.getFullYear(), qm, 1); e = now; break;
      case 'this_year':
        s = new Date(now.getFullYear(), 0, 1); e = now; break;
      case 'all_time':
        s = new Date(2020, 0, 1); e = now; break;
      default: return null;
    }
    return { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) };
  };

  const fetchStatement = async (sd, ed) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/project-accounts/${projectId}/statement`, {
        params: { start_date: sd, end_date: ed },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatement(resp.data);
    } catch { toast.error('Failed to load statement'); }
    finally { setLoading(false); }
  };

  const handleRangeChange = (preset) => {
    setDateRange(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      if (range) { setStartDate(range.start); setEndDate(range.end); fetchStatement(range.start, range.end); }
    }
  };

  useEffect(() => { handleRangeChange('all_time'); }, [projectId]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/project-accounts/${projectId}/statement/pdf`, {
        params: { start_date: startDate, end_date: endDate, filter_type: stmtFilter },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      const filterSuffix = stmtFilter === 'received' ? '_received' : stmtFilter === 'paid' ? '_paid' : '';
      a.download = `project_statement${filterSuffix}_${startDate}_to_${endDate}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  const filteredEntries = useMemo(() => {
    let entries = statement?.entries?.filter(e => {
      if (stmtFilter === 'all') return true;
      return e.type === stmtFilter;
    }) || [];
    // Reverse for newest-first display (backend returns oldest-first for running balance)
    if (stmtSortOrder === 'newest') {
      entries = [...entries].reverse();
    }
    return entries;
  }, [statement, stmtFilter, stmtSortOrder]);

  const handleRowClick = async (entry) => {
    if (!entry.transaction_id) return;
    try {
      const endpoint = entry.type === 'received'
        ? `${API}/payments/${entry.transaction_id}/receipt-pdf`
        : `${API}/vendor-payments/${entry.transaction_id}/receipt-pdf`;
      const resp = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${entry.transaction_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch {
      toast.error('Failed to download receipt');
    }
  };

  return (
    <div className="p-6" data-testid="project-statement-tab">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select value={dateRange} onValueChange={handleRangeChange}>
          <SelectTrigger className="w-40 h-9 text-sm" data-testid="project-statement-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="this_year">This Year</SelectItem>
            <SelectItem value="all_time">All Time</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
            {savedRanges.length > 0 && savedRanges.map(sr => (
              <SelectItem key={sr.id} value={sr.id}>{sr.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {dateRange === 'custom' && (
          <>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36 h-9 text-sm" data-testid="project-stmt-start" />
            <span className="text-stone-400">to</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36 h-9 text-sm" data-testid="project-stmt-end" />
            <Button size="sm" onClick={() => { if (startDate && endDate) fetchStatement(startDate, endDate); }} data-testid="project-stmt-generate">Generate</Button>
            {startDate && endDate && (
              savingRange ? (
                <div className="flex items-center gap-1">
                  <Input value={rangeName} onChange={(e) => setRangeName(e.target.value)} placeholder="e.g. FY 2025-26" className="w-36 h-8 text-sm" data-testid="save-range-name" onKeyDown={(e) => e.key === 'Enter' && saveCustomRange()} />
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveCustomRange} data-testid="save-range-confirm">Save</Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSavingRange(false)}>Cancel</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSavingRange(true)} data-testid="save-range-btn">
                  <Plus size={12} className="mr-1" /> Save Range
                </Button>
              )
            )}
          </>
        )}
        {/* Show saved ranges for quick deletion */}
        {savedRanges.length > 0 && dateRange !== 'custom' && (
          <div className="flex items-center gap-1 ml-2">
            {savedRanges.map(sr => (
              <span key={sr.id} className="inline-flex items-center gap-1 text-xs bg-stone-100 px-2 py-1 rounded-full text-stone-600">
                {sr.name}
                <button onClick={(e) => { e.stopPropagation(); deleteSavedRange(sr.id); }} className="text-stone-400 hover:text-red-500 ml-0.5" title="Remove saved range">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>
      {/* Filter + Download Row */}
      {statement && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden">
            {[
              { key: 'all', label: 'All' },
              { key: 'received', label: 'Client Received' },
              { key: 'paid', label: 'Vendor Paid' },
            ].map(f => (
              <button key={f.key} onClick={() => setStmtFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${stmtFilter === f.key ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                data-testid={`stmt-filter-${f.key}`}>{f.label}</button>
            ))}
          </div>
          <button onClick={() => setStmtSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg bg-white hover:bg-stone-50 transition-colors"
            data-testid="stmt-sort-toggle">
            <ArrowUpDown size={12} />
            {stmtSortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
          </button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={downloadPDF} disabled={downloading} data-testid="project-download-statement-pdf">
            <Download size={14} className="mr-1" />
            {downloading ? 'Downloading...' : stmtFilter === 'received' ? 'Download Received Statement' : stmtFilter === 'paid' ? 'Download Paid Statement' : 'Download Full Statement'}
          </Button>
        </div>
      )}

      {loading && <div className="p-8 text-center text-stone-400">Loading statement...</div>}

      {statement && !loading && (
        <div className="space-y-6">
          {/* Account Summary */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="bg-stone-900 text-white px-5 py-3">
              <h3 className="font-semibold">{statement.project_name} — Account Summary</h3>
              <p className="text-xs text-stone-400">{fmtDate(statement.period.start)} to {fmtDate(statement.period.end)}</p>
            </div>
            <div className="grid grid-cols-4 divide-x divide-stone-200">
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Opening Balance</p>
                <p className="text-lg font-bold text-stone-800">{'\u20B9'}{statement.opening_balance.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Vendor Paid (Debit)</p>
                <p className="text-lg font-bold text-red-700">{'\u20B9'}{statement.total_debits.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Client Received (Credit)</p>
                <p className="text-lg font-bold text-green-700">{'\u20B9'}{statement.total_credits.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center bg-stone-50">
                <p className="text-xs text-stone-500 mb-1">Closing Balance</p>
                <p className="text-lg font-bold text-stone-900">{'\u20B9'}{statement.closing_balance.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Party</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-right">Debit ({'\u20B9'})</th>
                  <th className="p-3 text-right">Credit ({'\u20B9'})</th>
                  <th className="p-3 text-right">Balance ({'\u20B9'})</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <td className="p-3 text-stone-500 italic" colSpan={4}>Opening Balance</td>
                  <td className="p-3 text-right">-</td>
                  <td className="p-3 text-right">-</td>
                  <td className="p-3 text-right font-semibold">{'\u20B9'}{statement.opening_balance.toLocaleString('en-IN')}</td>
                </tr>
                {filteredEntries.map((e, i) => (
                  <tr
                    key={i}
                    className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                    data-testid={`project-ledger-row-${i}`}
                    onClick={() => handleRowClick(e)}
                  >
                    <td className="p-3 text-stone-700 whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="p-3 text-stone-800 font-medium">
                      {e.vendor_id ? (
                        <button
                          type="button"
                          className="text-blue-700 hover:underline"
                          onClick={(ev) => { ev.stopPropagation(); navigate(`/vendors/${e.vendor_id}`); }}
                          data-testid={`project-ledger-party-link-${i}`}
                        >
                          {e.party_name || '-'}
                        </button>
                      ) : (e.party_name || '-')}
                    </td>
                    <td className="p-3 text-stone-700">
                      <span className={`inline-block px-1.5 py-0.5 text-xs rounded mr-2 ${e.type === 'received' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {e.type === 'received' ? 'Received' : 'Paid'}
                      </span>
                      {e.description}
                    </td>
                    <td className="p-3 text-stone-500">{e.details || '-'}</td>
                    <td className="p-3 text-right text-red-700 font-medium">{e.debit > 0 ? `${'\u20B9'}${e.debit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right text-green-700 font-medium">{e.credit > 0 ? `${'\u20B9'}${e.credit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right font-semibold text-stone-900">
                      <span className="inline-flex items-center gap-2">
                        {'\u20B9'}{e.balance.toLocaleString('en-IN')}
                        {e.transaction_id && <Download size={12} className="text-stone-400" />}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredEntries.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-stone-400">No transactions in this period</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-stone-800 text-white font-semibold">
                  <td colSpan={4} className="p-3 text-right">Closing Balance:</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.total_debits.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.total_credits.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.closing_balance.toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page Component ──────────────────────────────────────────────
export default function ProjectAccounts() {
  const token = localStorage.getItem('token');
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // iter-312q: honour ?projectId=… and ?tab=… so deep links from Edit BOQ → "Open ledger" land directly.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const pid = p.get('projectId');
    const tab = p.get('tab');
    if (pid) setSelectedId(pid);
    if (tab && ['overview', 'transactions', 'statement'].includes(tab)) setActiveTab(tab);
  }, []);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const resp = await axios.get(`${API}/project-accounts/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(resp.data);
      // iter-386u — Read URL projectId fresh here to avoid a stale closure
      // race with the deep-link useEffect (both fire on mount; closure
      // captured `selectedId = null`, wrongly picking projects[0]).
      const urlPid = new URLSearchParams(window.location.search).get('projectId');
      if (resp.data.length > 0 && !urlPid) {
        setSelectedId((prev) => prev || resp.data[0].id);
      }
    } catch (err) { 
      console.error('Project accounts list error:', err?.response?.status, err?.response?.data);
      const detail = err?.response?.data?.detail || 'Network error';
      toast.error(`Failed to load projects: ${detail}`); 
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedId) fetchOverview();
  }, [selectedId]);

  const fetchOverview = async () => {
    try {
      const resp = await axios.get(`${API}/project-accounts/${selectedId}/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(resp.data);
    } catch { toast.error('Failed to load project overview'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'statement', label: 'Ledger / Statement' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading project accounts...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex h-[calc(100vh-64px)]" data-testid="project-accounts-page">
        {/* Left Panel */}
        <ProjectListPanel
          projects={projects}
          selectedId={selectedId}
          onSelect={(id) => { setSelectedId(id); setActiveTab('overview'); }}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />

        {/* Right Panel */}
        <div className="flex-1 flex flex-col overflow-hidden bg-stone-50">
          {selectedId ? (
            <>
              {/* Tab Bar */}
              <div className="bg-white border-b border-stone-200 px-6 flex items-center gap-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-stone-900 text-stone-900'
                        : 'border-transparent text-stone-500 hover:text-stone-700'
                    }`}
                    data-testid={`project-tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
                <div className="flex-1" />
                <p className="text-sm font-medium text-stone-800">
                  {overview?.project?.name}
                </p>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'overview' && <OverviewTab data={overview} worksheetId={overview?.worksheet_id} />}
                {activeTab === 'transactions' && <TransactionsTab projectId={selectedId} />}
                {activeTab === 'statement' && <StatementTab projectId={selectedId} />}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-stone-400">
              <div className="text-center">
                <Briefcase size={48} className="mx-auto mb-4 text-stone-300" />
                <p className="font-medium">Select a project to view account summary</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
